import React, { useState, useEffect } from "react";
import "./marketControlBar.css";

const MILEAGE_VARIANCES = [2, 5, 10, 15, 20];
const RADIUS_OPTIONS_ACTIVE = [25, 50, 100, 250, 500];
const RADIUS_OPTIONS_SOLD = [25, 50, 100];

export default function MarketControlBar({ onFilterChange, initialFilters, hooptieMileage, isSoldComps = false }) {
  const defaultSortBy = isSoldComps ? "soldDate" : "price";
  const defaultSortOrder = isSoldComps ? "desc" : "asc";
  const [sortBy, setSortBy] = useState(initialFilters?.sortBy || defaultSortBy);
  const [sortOrder, setSortOrder] = useState(initialFilters?.sortOrder || defaultSortOrder);
  const [mileageRange, setMileageRange] = useState(initialFilters?.mileageRange || [0, 200000]);
  const [similarMileage, setSimilarMileage] = useState(initialFilters?.similarMileage || false);
  const [similarMileagePct, setSimilarMileagePct] = useState(15);
  const radiusOptions = isSoldComps ? RADIUS_OPTIONS_SOLD : RADIUS_OPTIONS_ACTIVE;
  const [radius, setRadius] = useState(() => {
    const r = initialFilters?.radius ?? 100;
    return isSoldComps && !RADIUS_OPTIONS_SOLD.includes(r) ? 100 : r;
  });
  const [independentDealersOnly, setIndependentDealersOnly] = useState(initialFilters?.independentDealersOnly || false);
  const [matchingVersionOnly, setMatchingVersionOnly] = useState(initialFilters?.matchingVersionOnly || false);
  const [matchingTransmissionOnly, setMatchingTransmissionOnly] = useState(initialFilters?.matchingTransmissionOnly || false);
  const [matchingDrivetrainOnly, setMatchingDrivetrainOnly] = useState(initialFilters?.matchingDrivetrainOnly || false);

  const minMileage = 0;
  const maxMileage = 500000; // high enough to include high-mileage comps when no filters selected

  const applySimilarMileageRange = (pct) => {
    if (!hooptieMileage) return;
    const low = Math.max(0, Math.floor(hooptieMileage * (1 - pct / 100)));
    const high = Math.ceil(hooptieMileage * (1 + pct / 100));
    setMileageRange([low, high]);
    setSimilarMileage(true);
  };

  const effectiveRadius = radiusOptions.includes(radius) ? radius : 100;

  useEffect(() => {
    onFilterChange?.({
      sortBy,
      sortOrder,
      mileageRange,
      similarMileage,
      radius: effectiveRadius,
      independentDealersOnly,
      matchingVersionOnly,
      matchingTransmissionOnly,
      matchingDrivetrainOnly,
    });
  }, [sortBy, sortOrder, mileageRange, similarMileage, effectiveRadius, independentDealersOnly, matchingVersionOnly, matchingTransmissionOnly, matchingDrivetrainOnly, onFilterChange]);

  const handleSortChange = (field) => {
    setSortBy(field);
  };

  const handleSortOrderToggle = () => {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const handleMileageChange = (index, value) => {
    const newRange = [...mileageRange];
    newRange[index] = parseInt(value);
    if (index === 0 && newRange[0] > newRange[1]) {
      newRange[1] = newRange[0];
    }
    if (index === 1 && newRange[1] < newRange[0]) {
      newRange[0] = newRange[1];
    }
    setMileageRange(newRange);
    setSimilarMileage(false);
  };

  const handleFilterToggle = (filterName) => {
    if (filterName === "similarMileage") {
      const newSimilarMileage = !similarMileage;
      setSimilarMileage(newSimilarMileage);
      if (newSimilarMileage && hooptieMileage) {
        applySimilarMileageRange(similarMileagePct);
      } else {
        setMileageRange([minMileage, maxMileage]);
      }
    } else if (filterName === "independentDealers") {
      setIndependentDealersOnly(!independentDealersOnly);
    } else if (filterName === "matchingVersion") {
      setMatchingVersionOnly(!matchingVersionOnly);
    } else if (filterName === "matchingTransmission") {
      setMatchingTransmissionOnly(!matchingTransmissionOnly);
    } else if (filterName === "matchingDrivetrain") {
      setMatchingDrivetrainOnly(!matchingDrivetrainOnly);
    }
  };

  const handleMileagePctChange = (pct) => {
    setSimilarMileagePct(pct);
    if (similarMileage && hooptieMileage) applySimilarMileageRange(pct);
  };

  const handleReset = () => {
    setSortBy(defaultSortBy);
    setSortOrder(defaultSortOrder);
    setMileageRange([minMileage, maxMileage]);
    setSimilarMileage(false);
    setRadius(100);
    setIndependentDealersOnly(false);
    setMatchingVersionOnly(false);
    setMatchingTransmissionOnly(false);
    setMatchingDrivetrainOnly(false);
  };

  return (
    <div className="market-control-bar">
      {/* Row 1: Sort Controls + Radius + Reset */}
      <div className="mcb-row-1">
        <div className="mcb-sort-group">
          <span className="mcb-label">Sort by</span>
          <select
            className="mcb-select"
            value={sortBy}
            onChange={(e) => handleSortChange(e.target.value)}
          >
            {isSoldComps && <option value="soldDate">Sold Date</option>}
            <option value="price">Price</option>
            <option value="mileage">Mileage</option>
            <option value="distance">Distance</option>
          </select>
          <button className="mcb-sort-toggle" onClick={handleSortOrderToggle}>
            {sortOrder === "asc" ? "↑" : "↓"} {sortOrder === "asc" ? "Asc" : "Desc"}
          </button>
        </div>
        <div className="mcb-row-1-right">
          <div className="mcb-radius-wrap">
            <span className="mcb-label">Radius:</span>
            <select
              className="mcb-select mcb-radius-select"
              value={effectiveRadius}
              onChange={(e) => setRadius(Number(e.target.value))}
              aria-label="Search radius in miles"
            >
              {radiusOptions.map((mi) => (
                <option key={mi} value={mi}>{mi} mi</option>
              ))}
            </select>
          </div>
          <button className="mcb-reset" onClick={handleReset}>
            Reset
          </button>
        </div>
      </div>

      {/* Row 2: Mileage Slider */}
      <div className="mcb-row-2">
        <span className="mcb-label">Mileage</span>
        <div 
          className="mcb-slider-container"
          style={{
            '--min-val': mileageRange[0],
            '--max-val': mileageRange[1],
            '--min-bound': minMileage,
            '--max-bound': maxMileage
          }}
        >
          <div className="mcb-slider-track">
            <input
              type="range"
              min={minMileage}
              max={maxMileage}
              value={mileageRange[0]}
              onChange={(e) => handleMileageChange(0, e.target.value)}
              className="mcb-slider mcb-slider-min"
            />
            <input
              type="range"
              min={minMileage}
              max={maxMileage}
              value={mileageRange[1]}
              onChange={(e) => handleMileageChange(1, e.target.value)}
              className="mcb-slider mcb-slider-max"
            />
          </div>
        </div>
        <div className="mcb-range-text">
          {mileageRange[0].toLocaleString()} – {mileageRange[1].toLocaleString()} mi
        </div>
      </div>

      {/* Row 3: Filter Chips — 2 on first row, 3 on second row */}
      <div className="mcb-row-3">
        <div className="mcb-row-3-line">
          <div
            className={`mcb-chip mcb-chip-mileage ${similarMileage ? "active" : ""}`}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <span
              onClick={() => handleFilterToggle("similarMileage")}
              style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              {similarMileage && <span className="mcb-chip-check">✓</span>}
              Similar Mileage
            </span>
            <select
              className="mcb-mileage-pct-select"
              value={similarMileagePct}
              onChange={(e) => handleMileagePctChange(Number(e.target.value))}
              onClick={(e) => e.stopPropagation()}
              aria-label="Mileage variance percent"
            >
              {MILEAGE_VARIANCES.map((p) => (
                <option key={p} value={p}>±{p}%</option>
              ))}
            </select>
          </div>
          <button
            className={`mcb-chip ${independentDealersOnly ? "active" : ""}`}
            onClick={() => handleFilterToggle("independentDealers")}
          >
            {independentDealersOnly && <span className="mcb-chip-check">✓</span>}
            Independent Dealers
          </button>
        </div>
        <div className="mcb-row-3-line">
          <button
            className={`mcb-chip ${matchingVersionOnly ? "active" : ""}`}
            onClick={() => handleFilterToggle("matchingVersion")}
          >
            {matchingVersionOnly && <span className="mcb-chip-check">✓</span>}
            Match Version
          </button>
          <button
            className={`mcb-chip ${matchingDrivetrainOnly ? "active" : ""}`}
            onClick={() => handleFilterToggle("matchingDrivetrain")}
          >
            {matchingDrivetrainOnly && <span className="mcb-chip-check">✓</span>}
            Match Drivetrain
          </button>
          <button
            className={`mcb-chip ${matchingTransmissionOnly ? "active" : ""}`}
            onClick={() => handleFilterToggle("matchingTransmission")}
          >
            {matchingTransmissionOnly && <span className="mcb-chip-check">✓</span>}
            Match Transmission
          </button>
        </div>
      </div>
    </div>
  );
}

